/**
 * @architecture-header 1
 * @layer module
 * @domain authentication
 * @responsibility Present isolated organization-student daily login and one-time activation forms.
 * @state-owner React Hook Form and component-local screen state
 * @allowed-imports React Hook Form; Zod resolver; app environment; shared i18n; same-module APIs and schemas
 * @forbidden direct Supabase access; route registration; token storage; authorization decisions
 * @public-api integration-owner auth module export
 * @side-effects cookie-backed FastAPI credential requests; removes consumed QR URL fragments
 * @tests frontend/packages/workspace-web/src/modules/auth/components/OrganizationStudentAuthScreen.test.tsx
 * @security-boundary unauthenticated browser to FastAPI organization credential endpoints
 * @data-classification passwords and one-time codes stay form-local
 * @trust-model a successful FastAPI response proves only the managed session; workspace authorization remains backend-owned
 */

import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useWorkspaceEnvironment } from '../../../app/config/environment';
import { useTranslator } from '../../../shared/model/use-workspace';
import {
  activateOrganizationCredential,
  loginWithOrganizationCredential,
  type OrganizationSession,
} from '../api/organization-credentials';
import {
  type OrganizationActivationValues,
  type OrganizationLoginValues,
  organizationActivationSchema,
  organizationLoginSchema,
  parseOrganizationCredentialFragment,
} from '../schemas/organization-credential';

export interface OrganizationStudentAuthScreenProps {
  initialMode?: 'login' | 'activate';
  legal: { consentRequired: boolean; privacyVersion: string; termsVersion: string };
  onAuthenticated(session: OrganizationSession): void | Promise<void>;
}

export function OrganizationStudentAuthScreen({
  initialMode = 'login',
  legal,
  onAuthenticated,
}: OrganizationStudentAuthScreenProps) {
  const environment = useWorkspaceEnvironment();
  const t = useTranslator();
  const [mode, setMode] = useState<'login' | 'activate'>(initialMode);
  const [error, setError] = useState<string | null>(null);
  const login = useForm<OrganizationLoginValues>({
    resolver: zodResolver(organizationLoginSchema),
  });
  const activation = useForm<OrganizationActivationValues>({
    resolver: zodResolver(organizationActivationSchema),
    defaultValues: {
      acceptLegal: legal.consentRequired ? undefined : true,
      confirmPassword: '',
      loginCode: '',
      newPassword: '',
      oneTimeCode: '',
    },
  });

  useEffect(() => {
    const fragment = window.location.hash;
    if (!fragment) return;
    const parsed = parseOrganizationCredentialFragment(fragment);
    window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}`);
    if (!parsed) return;
    activation.reset({
      ...parsed,
      acceptLegal: legal.consentRequired ? undefined : true,
      confirmPassword: '',
      newPassword: '',
    });
    setMode('activate');
  }, [activation, legal.consentRequired]);

  const reportError = (cause: unknown) => {
    setError(t(cause instanceof Error ? cause.message : '暫時無法完成登入，請稍後再試。'));
  };

  const submitLogin = async (values: OrganizationLoginValues) => {
    setError(null);
    try {
      await onAuthenticated(await loginWithOrganizationCredential(environment.apiClient, values));
    } catch (cause) {
      reportError(cause);
    }
  };

  const submitActivation = async (values: OrganizationActivationValues) => {
    setError(null);
    try {
      await onAuthenticated(
        await activateOrganizationCredential(environment.apiClient, values, legal),
      );
    } catch (cause) {
      reportError(cause);
    }
  };

  return (
    <main className="entry-screen">
      <div className="joinbox" style={{ width: 'min(420px, 92vw)' }}>
        <div className="jlogo">V</div>
        <h3>{mode === 'login' ? t('使用機構帳號登入') : t('啟用機構帳號')}</h3>
        {mode === 'login' ? (
          <form
            onSubmit={login.handleSubmit(submitLogin)}
            style={{ marginTop: 14, textAlign: 'left' }}
          >
            <label className="sec5" htmlFor="organization-login-code">
              {t('機構帳號')}
            </label>
            <input
              autoComplete="username"
              id="organization-login-code"
              style={{ width: '100%', padding: '9px 12px' }}
              {...login.register('loginCode')}
            />
            {login.formState.errors.loginCode ? (
              <p className="jmsg">{t(login.formState.errors.loginCode.message ?? '')}</p>
            ) : null}
            <label className="sec5" htmlFor="organization-password">
              {t('密碼')}
            </label>
            <input
              autoComplete="current-password"
              id="organization-password"
              style={{ width: '100%', padding: '9px 12px' }}
              type="password"
              {...login.register('password')}
            />
            {login.formState.errors.password ? (
              <p className="jmsg">{t(login.formState.errors.password.message ?? '')}</p>
            ) : null}
            {error ? <p className="jmsg">{error}</p> : null}
            <button
              className="bt pri"
              disabled={login.formState.isSubmitting}
              style={{ marginTop: 16, width: '100%' }}
              type="submit"
            >
              {login.formState.isSubmitting ? t('登入中…') : t('登入')}
            </button>
            <button
              className="bt"
              style={{ marginTop: 10, width: '100%' }}
              type="button"
              onClick={() => {
                setError(null);
                setMode('activate');
              }}
            >
              {t('使用啟用碼或重設碼')}
            </button>
          </form>
        ) : (
          <form
            onSubmit={activation.handleSubmit(submitActivation)}
            style={{ marginTop: 14, textAlign: 'left' }}
          >
            <label className="sec5" htmlFor="activation-login-code">
              {t('機構帳號')}
            </label>
            <input
              autoComplete="username"
              id="activation-login-code"
              style={{ width: '100%', padding: '9px 12px' }}
              {...activation.register('loginCode')}
            />
            <label className="sec5" htmlFor="activation-one-time-code">
              {t('一次性代碼')}
            </label>
            <input
              autoComplete="one-time-code"
              id="activation-one-time-code"
              style={{ width: '100%', padding: '9px 12px' }}
              {...activation.register('oneTimeCode')}
            />
            <label className="sec5" htmlFor="activation-new-password">
              {t('新密碼')}
            </label>
            <input
              autoComplete="new-password"
              id="activation-new-password"
              style={{ width: '100%', padding: '9px 12px' }}
              type="password"
              {...activation.register('newPassword')}
            />
            <label className="sec5" htmlFor="activation-confirm-password">
              {t('確認新密碼')}
            </label>
            <input
              autoComplete="new-password"
              id="activation-confirm-password"
              style={{ width: '100%', padding: '9px 12px' }}
              type="password"
              {...activation.register('confirmPassword')}
            />
            {legal.consentRequired ? (
              <label style={{ display: 'block', marginTop: 14 }}>
                <input type="checkbox" {...activation.register('acceptLegal')} />{' '}
                {t('我已閱讀並同意服務條款與隱私權政策')}
              </label>
            ) : null}
            {activation.formState.errors.loginCode ||
            activation.formState.errors.oneTimeCode ||
            activation.formState.errors.newPassword ||
            activation.formState.errors.confirmPassword ||
            activation.formState.errors.acceptLegal ? (
              <p className="jmsg">
                {t(
                  activation.formState.errors.loginCode?.message ??
                    activation.formState.errors.oneTimeCode?.message ??
                    activation.formState.errors.newPassword?.message ??
                    activation.formState.errors.confirmPassword?.message ??
                    activation.formState.errors.acceptLegal?.message ??
                    '',
                )}
              </p>
            ) : null}
            {error ? <p className="jmsg">{error}</p> : null}
            <button
              className="bt pri"
              disabled={activation.formState.isSubmitting}
              style={{ marginTop: 16, width: '100%' }}
              type="submit"
            >
              {activation.formState.isSubmitting ? t('設定中…') : t('設定密碼並登入')}
            </button>
            <p className="jmsg">{t('請找櫃檯或老師重發啟用碼')}</p>
          </form>
        )}
      </div>
    </main>
  );
}
